// src/security/mfaContract.ts
/**
 * The wire shapes of `/api/v1/mfa/**` and `/api/admin/security/**`, plus the string handling every
 * TOTP screen shares.
 *
 * WHY THIS MODULE IS PURE. It imports nothing, on purpose, for the same two reasons as
 * `api/errors.ts`: jest 27 (CRA 5) cannot resolve axios 1.7.8's ESM entry, and importing the Redux
 * store has side effects (localStorage, the whole state tree) the moment the module loads. The
 * normalisers below are the part of this feature a test can actually pin down — an admin retyping a
 * printed recovery code is the failure mode this file exists to absorb — so they live where a test
 * can reach them without an HTTP client or a store.
 *
 * It is also the single place the client and the server agree on a shape. Nothing here is checked by
 * a compiler against the Java DTOs; Jackson decides the wire name, not the Java field name (see
 * CLAUDE.md), so every type below is written from the DTO's serialised form and names the DTO it
 * mirrors.
 */

/* ---------------- status ---------------- */

/**
 * `GET /api/v1/mfa/status`, mirroring `TotpStatusResponse`.
 *
 * `sessionVerified` is per TOKEN, not per account — it lives in `t_token.c_mfa_verified`, and every
 * login mints fresh rows. It must therefore be fetched after each login and never persisted.
 *
 * `recoveryCodesRemaining` is `null` for "not known", which is what an older jar answering the
 * two-field body produces. Screens render that as "—" rather than as zero, because zero remaining
 * codes is a warning worth showing and "we did not ask" is not.
 */
export interface MfaStatus {
    enabled: boolean;
    sessionVerified: boolean;
    enforced: boolean;
    recoveryCodesRemaining: number | null;
}

/**
 * Tolerant of both status bodies: the two-field `{enabled, sessionVerified}` a jar without the
 * status DTO answers, and the four-field `{enabled, sessionVerified, enforced,
 * recoveryCodesRemaining}` this UI was built against.
 *
 * A missing `enforced` reads as false. That is the safe direction and not merely a default: the UI
 * only ever *displays* this flag, and nothing gates on it — whether a request is refused for want of
 * step-up is decided by the server's own 403, never by this field. Showing "vynucovanie vypnuté"
 * against a jar that cannot report the flag is a stale label; gating on a guess would be a bug.
 *
 * Every field is compared against `true` rather than coerced, so a body that arrives as a string, a
 * number or `null` cannot read as enabled.
 */
export function parseMfaStatus(data: any): MfaStatus {
    const body = data && typeof data === 'object' ? data : {};
    const remaining = body.recoveryCodesRemaining;
    return {
        enabled: body.enabled === true,
        sessionVerified: body.sessionVerified === true,
        enforced: body.enforced === true,
        recoveryCodesRemaining: typeof remaining === 'number' && isFinite(remaining) ? remaining : null,
    };
}

/* ---------------- enrolment ---------------- */

/**
 * `POST /api/v1/mfa/enroll`, mirroring `TotpEnrollResponse`.
 *
 * `otpAuthUri` contains the shared secret in the clear. It is rendered as a QR code in this browser
 * (`qrcode.react`) and never handed to a third-party image service.
 */
export interface TotpEnrollment {
    otpAuthUri: string;
    base32Secret: string;
}

/** `POST /api/v1/mfa/verify-enrollment` and `/recovery-codes/regenerate`, mirroring
 *  `TotpRecoveryCodesResponse`. The only moment these codes exist in plaintext outside the admin's
 *  own password manager: the server stores nothing but BCrypt hashes and never re-issues them. */
export interface RecoveryCodesBody {
    recoveryCodes: string[];
}

/* ---------------- sessions ---------------- */

/**
 * One row of `GET /api/admin/security/sessions`, mirroring `ActiveSessionDto`.
 *
 * `createdAt` is a zone-less `LocalDateTime` serialised as an ISO-like string in SERVER time
 * (`2026-09-10T03:08:12.123456`) — typed as a string, never a Date, because the fractional part can
 * carry six digits and browsers disagree about parsing that.
 *
 * One login produces two rows for the same user, a BEARER and a REFRESH, seconds apart; grouping
 * them back into one line is the session list's own job.
 */
export interface ActiveSession {
    tokenId: number;
    userId: number;
    userEmail: string;
    tokenType: 'BEARER' | 'REFRESH';
    createdAt: string | null;
    mfaVerified: boolean;
}

/** `{revoked}` — the body of the three revoke endpoints that count what they ended. */
export interface RevokedCount {
    revoked: number;
}

/* ---------------- TOTP codes ---------------- */

export const TOTP_CODE_LENGTH = 6;

/**
 * Digits only, capped at six. Authenticator apps show the code in two groups of three, so it gets
 * copied in with a space in the middle at least as often as without one, and a paste can carry a
 * trailing newline.
 */
export function normalizeTotpCode(raw: string): string {
    const text = typeof raw === 'string' ? raw : '';
    return text.replace(/\D+/g, '').slice(0, TOTP_CODE_LENGTH);
}

/** Whether the field holds a whole code — what auto-submit and the submit button both key off. */
export function isTotpCodeComplete(raw: string): boolean {
    return normalizeTotpCode(raw).length === TOTP_CODE_LENGTH;
}

/* ---------------- recovery codes ---------------- */

/** Mirrors `TotpController.RECOVERY_CODE_ALPHABET`. Excludes 0, O, 1, I and L — these are read off a
 *  screen or a printout by hand, once, under pressure. */
export const RECOVERY_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
/** Mirrors `TotpController.RECOVERY_CODE_LENGTH`: eight characters, shown as `ABCD-EFGH`. */
export const RECOVERY_CODE_LENGTH = 8;
/** Mirrors `TotpController.RECOVERY_CODE_COUNT`: how many a fresh set contains. */
export const RECOVERY_CODE_COUNT = 10;

/**
 * The form the server compares against: upper-case, no dashes, no whitespace.
 *
 * The server's own normaliser is `trim().toUpperCase().replace("-", "")`, which does NOT remove an
 * interior space — so a code typed as "ABCD EFGH" fails there and succeeds here. Normalising on this
 * side produces a string that normaliser leaves untouched, which is the point: the client is
 * strictly more forgiving and the two can never disagree about what was submitted.
 */
export function normalizeRecoveryCode(raw: string): string {
    const text = typeof raw === 'string' ? raw : '';
    return text.replace(/[\s-]+/g, '').toUpperCase();
}

/** Whether this could be a recovery code at all: eight characters, every one of them in the
 *  alphabet. Cheap client-side rejection of a typo — the recovery endpoint is BCrypt-slow (~1 s) and
 *  allows five attempts per fifteen minutes, so spending one on `AB12CD30` is a real cost. */
export function isRecoveryCodeShape(raw: string): boolean {
    const code = normalizeRecoveryCode(raw);
    if (code.length !== RECOVERY_CODE_LENGTH) {
        return false;
    }
    for (let i = 0; i < code.length; i++) {
        if (RECOVERY_CODE_ALPHABET.indexOf(code.charAt(i)) === -1) {
            return false;
        }
    }
    return true;
}

/** `ABCD-EFGH` for display. Anything that is not exactly eight normalised characters comes back
 *  normalised but unformatted, so a code still being typed never grows a dash mid-keystroke. */
export function formatRecoveryCode(raw: string): string {
    const code = normalizeRecoveryCode(raw);
    if (code.length !== RECOVERY_CODE_LENGTH) {
        return code;
    }
    const half = RECOVERY_CODE_LENGTH / 2;
    return code.slice(0, half) + '-' + code.slice(half);
}

/* ---------------- rate-limit hints ---------------- */

/**
 * The windows `TotpController` enforces, in words. A bare "Priveľa pokusov" leaves an admin locked
 * out of their own site with no idea whether to wait one minute or one hour, and these windows
 * differ.
 *
 * `/verify` and `/verify-enrollment` spend TWO budgets and answer the same `429 rate_limited` from
 * either, indistinguishably on the wire, so this sentence has to name both or it is wrong half the
 * time: `VERIFY_ATTEMPTS_PER_WINDOW` / `VERIFY_WINDOW_MILLIS` (5 per 5 minutes, keyed on the client
 * address) and `ACCOUNT_VERIFY_ATTEMPTS` / `ACCOUNT_VERIFY_WINDOW_MILLIS` (10 per 15 minutes, keyed
 * on the account and SHARED by those two endpoints). The account budget is charged before the code
 * is judged, so successful verifications spend it too — an admin who enrolled, mistyped twice and
 * then verified again on a second device has spent four of ten inside a quarter of an hour without
 * anyone having attacked him. Told only about the five-minute window he waits five minutes, meets
 * this identical sentence again, and has ten more to go with nothing on screen explaining it, on the
 * one screen he can still reach while locked out.
 *
 * `/recover` is deliberately outside the account budget (BCrypt-slow, ~40 bits, and it is the way
 * back in while those ten are gone), so its hint stays one window — the one that matches the login
 * lockout.
 *
 * If any of those four constant pairs moves, these two strings move with it; `mfaContract.test.ts`
 * pins the numbers so that the next window change cannot land quietly here.
 */
export const VERIFY_RATE_LIMIT_HINT = 'Limit je 5 pokusov za 5 minút a 10 pokusov za 15 minút.';
export const RECOVER_RATE_LIMIT_HINT = 'Limit je 5 pokusov za 15 minút.';
